// "Parcelles" tab for a single delivery form. Mirrors the OT parcels
// tab's UX exactly — Mapbox map with floating prev/next overlay and a
// collapsible table below — but pulls geo from the delivery-form
// /traceability/geo endpoint so the polygon set matches the producers
// on this truckload only.

import { useMemo, useState } from "react"
import { ChevronDown, ChevronUp, Loader2, MapPin } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import { useDeliveryFormTraceabilityGeo } from "@/features/delivery-forms/hooks/use-delivery-form"
import { ParcelsNavOverlay } from "@/features/transit/components/traceability/parcels-nav"
import { TraceabilityParcelsMap } from "@/features/transit/components/traceability/parcels-map"
import type {
  OTTraceability,
  ParcelGeoEntry,
  TraceabilityParcelEntry,
} from "@/features/transit/api/types"

interface Props {
  deliveryFormId: number
  traceability: OTTraceability
  // Whether the tab is currently visible; gates the geo fetch.
  active: boolean
}

export function DeliveryParcelsTab({
  deliveryFormId,
  traceability,
  active,
}: Props) {
  const { t } = useTranslation("transit")
  // null = "use the first parcel by default" — derived at render time.
  const [userSelectedId, setUserSelectedId] = useState<number | null>(null)
  const [listOpen, setListOpen] = useState(false)

  const geo = useDeliveryFormTraceabilityGeo(deliveryFormId, {
    enabled: active,
  })

  const parcelsById = useMemo(() => {
    const out = new Map<number, TraceabilityParcelEntry>()
    for (const p of traceability.parcels) out.set(p.id, p)
    return out
  }, [traceability.parcels])

  const enriched = useMemo(() => {
    const geoById = new Map<number, ParcelGeoEntry>()
    for (const p of geo.data?.parcels ?? []) geoById.set(p.id, p)
    return traceability.parcels.map((trace) => {
      const g = geoById.get(trace.id)
      return {
        ...trace,
        centroid: g?.centroid ?? trace.centroid,
        locations: g?.locations ?? null,
      }
    })
  }, [traceability.parcels, geo.data])

  const navParcels = useMemo(
    () =>
      enriched.filter(
        (p) => Array.isArray(p.locations) && p.locations.length > 0,
      ),
    [enriched],
  )

  const geoForMap: ParcelGeoEntry[] = useMemo(
    () =>
      enriched.map((p) => ({
        id: p.id,
        parcelId: p.parcelId,
        internalId: p.internalId,
        farmerId: p.farmerId,
        centroid: p.centroid,
        hectares: p.hectares,
        status: p.status,
        isAtRiskDeforested: p.isAtRiskDeforested,
        locations: p.locations,
      })),
    [enriched],
  )

  const selectedId =
    userSelectedId ?? (navParcels.length > 0 ? navParcels[0].id : null)

  const selectedIndex = useMemo(() => {
    if (selectedId == null) return 0
    const i = navParcels.findIndex((p) => p.id === selectedId)
    return i >= 0 ? i : 0
  }, [navParcels, selectedId])

  const goPrev = () => {
    if (navParcels.length === 0) return
    const next = (selectedIndex - 1 + navParcels.length) % navParcels.length
    setUserSelectedId(navParcels[next].id)
  }
  const goNext = () => {
    if (navParcels.length === 0) return
    const next = (selectedIndex + 1) % navParcels.length
    setUserSelectedId(navParcels[next].id)
  }

  return (
    <div className="flex flex-col gap-4">
      {geo.isLoading ? (
        <Card className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span>{t("trace.parcels.mapLoading")}</span>
        </Card>
      ) : (
        <TraceabilityParcelsMap
          parcels={geoForMap}
          selectedId={selectedId}
          onSelect={(p) => setUserSelectedId(p.id)}
        >
          {navParcels.length > 0 ? (
            <ParcelsNavOverlay
              parcels={navParcels.map((p) => ({
                id: p.id,
                parcelId: p.parcelId,
                internalId: p.internalId,
              }))}
              selectedIndex={selectedIndex}
              onPrev={goPrev}
              onNext={goNext}
              listOpen={listOpen}
              onToggleList={() => setListOpen((v) => !v)}
            />
          ) : null}
        </TraceabilityParcelsMap>
      )}

      <Card className="overflow-hidden p-0">
        <button
          type="button"
          onClick={() => setListOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 border-b px-4 py-3 text-left transition-colors hover:bg-muted/40"
          aria-expanded={listOpen}
        >
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold tracking-tight text-foreground">
              {t("trace.parcels.tableTitle", { count: enriched.length })}
            </h2>
          </div>
          {listOpen ? (
            <ChevronUp className="size-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="size-4 text-muted-foreground" />
          )}
        </button>
        {listOpen ? (
          enriched.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t("trace.parcels.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.id")}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      {t("trace.parcels.col.size")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.region")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.deforestation")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.status")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("trace.parcels.col.compliance")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {enriched.map((parcel) => {
                    const isSelected = selectedId === parcel.id
                    return (
                      <tr
                        key={parcel.id}
                        className={`cursor-pointer hover:bg-muted/40 ${
                          isSelected ? "bg-amber-50 dark:bg-amber-500/10" : ""
                        }`}
                        onClick={() =>
                          setUserSelectedId(isSelected ? null : parcel.id)
                        }
                      >
                        <td className="px-3 py-2 font-medium text-foreground">
                          {parcel.parcelId ??
                            parcel.internalId ??
                            `#${parcel.id}`}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {parcel.hectares.toFixed(2)} ha
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {parcelsById.get(parcel.id)?.regionName ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          <YesNoChip
                            flag={parcel.isAtRiskDeforested}
                            okLabel={t("trace.parcels.deforestOk")}
                            koLabel={t("trace.parcels.deforestKo")}
                            inverted
                          />
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-muted-foreground">
                            {parcel.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {parcelsById.get(parcel.id)?.isCompliant ? (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              {t("trace.parcels.statusOk")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
                              {t("trace.parcels.statusRisk")}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Card>
    </div>
  )
}

function YesNoChip({
  flag,
  okLabel,
  koLabel,
  inverted,
}: {
  flag: boolean
  okLabel: string
  koLabel: string
  inverted?: boolean
}) {
  const ok = inverted ? !flag : flag
  return ok ? (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
      {okLabel}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
      {koLabel}
    </span>
  )
}
